import React, { useState } from 'react';
import { LotCalcule, StatutTS, SituationSousTraitant } from '../types';
import ProjetProgressBar from './ProjetProgressBar';
import TravauxSupplementairesSection from './TravauxSupplementairesSection';
import SousTraitantSection from './SousTraitantSection';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface LotRowProps {
  lot: LotCalcule;
  onAjouterSituation: (lotId: string) => void;
  onAjouterTS: (lotId: string, ts: {
    intitule: string;
    montant: number;
    commentaire?: string;
    dateAjout: string;
    statut: StatutTS;
  }) => void;
  onAjouterSituationST?: (lotId: string, situation: Omit<SituationSousTraitant, 'id'>) => void;
}

const fmt = (n: number) => n.toLocaleString('fr-FR');

const statutBadge: Record<string, { bg: string; text: string; label: string }> = {
  non_demarre: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Non démarré' },
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
  termine: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
  en_retard: { bg: 'bg-red-100', text: 'text-red-800', label: 'En retard' },
};

const LotRow: React.FC<LotRowProps> = ({ lot, onAjouterSituation, onAjouterTS, onAjouterSituationST }) => {
  const [expanded, setExpanded] = useState(false);
  const badge = statutBadge[lot.enRetard ? 'en_retard' : lot.statut] || statutBadge.non_demarre;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Ligne résumé */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {expanded ? (
            <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">{lot.nom}</div>
            <div className="text-sm text-gray-500">{lot.entreprise}</div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right hidden md:block">
            <div className="text-sm text-gray-500">Contractuel</div>
            <div className="font-semibold text-gray-900">{fmt(lot.montantSigne)} €</div>
          </div>
          <div className="text-right hidden md:block">
            <div className="text-sm text-gray-500">Facturé</div>
            <div className="font-semibold text-gray-900">{fmt(lot.cumulSituations)} €</div>
          </div>
          <div className="text-right hidden md:block">
            <div className="flex items-center justify-end gap-1 text-sm text-gray-500">
              MAT
              {lot.mat.tendance === 'amelioration' && <ArrowTrendingUpIcon className="w-3 h-3 text-emerald-500" />}
              {lot.mat.tendance === 'degradation' && <ArrowTrendingDownIcon className="w-3 h-3 text-red-500" />}
              {lot.mat.tendance === 'stable' && <MinusIcon className="w-3 h-3 text-gray-400" />}
            </div>
            <div className={`font-semibold ${lot.mat.margeATerminaison >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {lot.mat.margeATerminaison >= 0 ? '+' : ''}{fmt(Math.round(lot.mat.margeATerminaison))} €
            </div>
          </div>
          <div className="w-32 hidden md:block">
            <ProjetProgressBar
              pourcentage={lot.pourcentageAvancement}
              color={lot.enRetard ? 'bg-red-500' : 'bg-violet-600'}
              height="h-2"
            />
            <div className="text-xs text-gray-500 text-right mt-0.5">{lot.pourcentageAvancement}%</div>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${badge.bg} ${badge.text} flex-shrink-0`}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* Détails expandables */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          {/* Données financières — 3 lignes */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-500">Montant contractuel</div>
              <div className="font-semibold text-gray-900">{fmt(lot.montantSigne)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Coefficient</div>
              <div className="font-semibold text-gray-900">×{lot.coefficient.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Prix de vente</div>
              <div className="font-semibold text-gray-900">{fmt(Math.round(lot.prixDeVente))} €</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-500">Total TS validés</div>
              <div className="font-semibold text-gray-900">{fmt(lot.totalTS)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Budget modifié</div>
              <div className="font-semibold text-gray-900">{fmt(lot.budgetModifie)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">PV modifié</div>
              <div className="font-semibold text-gray-900">{fmt(Math.round(lot.prixDeVenteModifie))} €</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <div className="text-sm text-gray-500">Avancement facturé</div>
              <div className="font-semibold text-gray-900">{fmt(lot.cumulSituations)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Reste à payer</div>
              <div className="font-semibold text-gray-900">{fmt(lot.resteAPayer)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Marge</div>
              <div className={`font-semibold ${lot.margeActuelle >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {lot.margeActuelle >= 0 ? '+' : ''}{fmt(Math.round(lot.margeActuelle))} €
                <span className="text-xs ml-1">({lot.tauxMarge.toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {/* 4e ligne financière : MAT */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <div className="text-sm text-gray-500">Coût prévisionnel</div>
              <div className="font-semibold text-gray-900">{fmt(Math.round(lot.mat.coutPrevisionnel))} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">PV à terminaison</div>
              <div className="font-semibold text-gray-900">{fmt(Math.round(lot.mat.prixDeVenteTerminaison))} €</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                MAT
                {lot.mat.tendance === 'amelioration' && <ArrowTrendingUpIcon className="w-3 h-3 text-emerald-500" />}
                {lot.mat.tendance === 'degradation' && <ArrowTrendingDownIcon className="w-3 h-3 text-red-500" />}
                {lot.mat.tendance === 'stable' && <MinusIcon className="w-3 h-3 text-gray-400" />}
              </div>
              <div className={`font-semibold ${lot.mat.margeATerminaison >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {lot.mat.margeATerminaison >= 0 ? '+' : ''}{fmt(Math.round(lot.mat.margeATerminaison))} €
                <span className="text-xs ml-1">({lot.mat.tauxMAT.toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {/* Indicateurs secondaires */}
          {(lot.productivite > 0 || lot.deriveTemporelle > 0 || lot.ratioDebourse > 0 || (lot.sousTraitant && lot.tauxAcceptation < 100)) && (
            <div className="flex items-center gap-4 mb-6 px-3 py-2 bg-gray-100 rounded-lg text-[11px] text-gray-500">
              <div className="flex items-center gap-1">
                <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-medium text-gray-600">Indicateurs</span>
              </div>
              {lot.productivite > 0 && (
                <div className="relative group/prod">
                  <span className={`font-medium ${lot.productivite >= 90 ? 'text-emerald-600' : lot.productivite >= 70 ? 'text-amber-600' : 'text-red-500'}`}>
                    Productivité {lot.productivite}%
                  </span>
                  <div className="absolute bottom-full left-0 mb-1 w-48 bg-gray-900 text-white text-[10px] rounded-lg p-2 shadow-lg opacity-0 invisible group-hover/prod:opacity-100 group-hover/prod:visible transition-all z-50 pointer-events-none">
                    Ratio avancement réel / temps écoulé. 100% = dans les temps.
                  </div>
                </div>
              )}
              {lot.deriveTemporelle > 0 && (
                <span className="text-red-500 font-medium">
                  Retard {lot.deriveTemporelle} sem.
                </span>
              )}
              {lot.ratioDebourse > 0 && (
                <div className="relative group/ratio">
                  <span className="text-gray-600">
                    Déboursé {lot.ratioDebourse.toFixed(0)}%
                  </span>
                  <div className="absolute bottom-full left-0 mb-1 w-48 bg-gray-900 text-white text-[10px] rounded-lg p-2 shadow-lg opacity-0 invisible group-hover/ratio:opacity-100 group-hover/ratio:visible transition-all z-50 pointer-events-none">
                    Part des coûts directs (MO + matériaux + matériel) sur le budget modifié.
                  </div>
                </div>
              )}
              {lot.sousTraitant && lot.tauxAcceptation < 100 && (
                <div className="relative group/taux">
                  <span className={`font-medium ${lot.tauxAcceptation >= 90 ? 'text-emerald-600' : lot.tauxAcceptation >= 75 ? 'text-amber-600' : 'text-red-500'}`}>
                    Acceptation ST {lot.tauxAcceptation}%
                  </span>
                  <div className="absolute bottom-full left-0 mb-1 w-48 bg-gray-900 text-white text-[10px] rounded-lg p-2 shadow-lg opacity-0 invisible group-hover/taux:opacity-100 group-hover/taux:visible transition-all z-50 pointer-events-none">
                    Taux d'acceptation des situations sous-traitant (accepté / demandé).
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ventilation coûts */}
          {lot.ventilationCouts && (
            <div className="mb-6 p-3 bg-white rounded-lg border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ventilation des coûts</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Main d'oeuvre</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(lot.ventilationCouts.debourseSecMain)} €</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Matériaux</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(lot.ventilationCouts.debourseSecMateriaux)} €</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Matériel</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(lot.ventilationCouts.debourseSecMateriel)} €</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Frais généraux</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(lot.ventilationCouts.fraisGeneraux)} €</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Encadrement</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(lot.ventilationCouts.fraisEncadrement)} €</div>
                </div>
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="text-sm text-gray-500">Début prévu</div>
              <div className="text-sm font-medium text-gray-900">{new Date(lot.dateDebutPrevue).toLocaleDateString('fr-FR')}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Fin prévue</div>
              <div className="text-sm font-medium text-gray-900">{new Date(lot.dateFinPrevue).toLocaleDateString('fr-FR')}</div>
            </div>
            {lot.dateDebutReelle && (
              <div>
                <div className="text-sm text-gray-500">Début réel</div>
                <div className="text-sm font-medium text-gray-900">{new Date(lot.dateDebutReelle).toLocaleDateString('fr-FR')}</div>
              </div>
            )}
            {lot.dateFinReelle && (
              <div>
                <div className="text-sm text-gray-500">Fin réelle</div>
                <div className="text-sm font-medium text-gray-900">{new Date(lot.dateFinReelle).toLocaleDateString('fr-FR')}</div>
              </div>
            )}
          </div>

          {/* Situations */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700">Situations de travaux</h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAjouterSituation(lot.id);
                }}
                className="flex items-center text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
              >
                <PlusCircleIcon className="w-4 h-4 mr-1" />
                Nouvelle situation
              </button>
            </div>

            {lot.situations.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="py-2 px-3">N°</th>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3 text-right">Montant cumulé</th>
                    <th className="py-2 px-3 text-right">%</th>
                    <th className="py-2 px-3">Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  {lot.situations.map(sit => (
                    <tr key={sit.id} className="border-b border-gray-100 last:border-b-0">
                      <td className="py-2 px-3 text-sm font-medium text-gray-900">{sit.numero}</td>
                      <td className="py-2 px-3 text-sm text-gray-700">
                        {new Date(sit.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-2 px-3 text-sm text-right font-semibold text-gray-900">
                        {fmt(sit.montantCumulatif)} €
                      </td>
                      <td className="py-2 px-3 text-sm text-right text-gray-700">
                        {sit.pourcentageAvancement != null ? `${sit.pourcentageAvancement}%` : '—'}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {sit.commentaire || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-400 italic">Aucune situation enregistrée</p>
            )}
          </div>

          {/* Travaux Supplémentaires */}
          <TravauxSupplementairesSection
            travauxSupplementaires={lot.travauxSupplementaires}
            onAjouter={(ts) => onAjouterTS(lot.id, ts)}
          />

          {/* Sous-traitant */}
          {lot.sousTraitant && (
            <SousTraitantSection
              sousTraitant={lot.sousTraitant}
              onAjouterSituation={onAjouterSituationST
                ? (situation) => onAjouterSituationST(lot.id, situation)
                : undefined
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LotRow;
